import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from 'src/api/root'
import { projectRouterKeys } from 'src/api/routes/project'
import { useTenantStore } from '../store/tenentStore'

// A deep link carries no tenant, so a project outside the tab's tenant opens in the user's tenant that holds it.
export const useProjectTenantFallback = (
  projectId: string,
  { hasProject, isFetching: isFetchingProject }: { hasProject: boolean; isFetching: boolean },
) => {
  const queryClient = useQueryClient()
  const { currentTenant, setCurrentTenant } = useTenantStore()
  // Once the project has loaded or we switched once, a miss means a manual workspace switch or no access: show the error.
  const settledForProjectId = useRef<string>()
  const canSwitch = !hasProject && !isFetchingProject && settledForProjectId.current !== projectId
  const { data: tenantsWithProject, isFetching } = api.tenant.myTenantsWithProject.useQuery(projectId, {
    enabled: canSwitch,
  })
  const targetTenant = canSwitch ? tenantsWithProject?.find((tenant) => tenant.id !== currentTenant?.id) : undefined

  useEffect(() => {
    if (hasProject) settledForProjectId.current = projectId
  }, [hasProject, projectId])

  useEffect(() => {
    if (!targetTenant) return
    settledForProjectId.current = projectId
    setCurrentTenant(targetTenant)
    queryClient.invalidateQueries({ queryKey: [projectRouterKeys.detail, projectId] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetTenant?.id])

  return { isSwitchingTenant: canSwitch && (isFetching || Boolean(targetTenant)) }
}
